import { BEHAVIORAL_CODES } from '../components/studentLabelingConfig'

const getBehavior = (id) => BEHAVIORAL_CODES.find(b => b.id === id)

const CODE_EVENTS = [
  'CODE_TYPE', 'CODE_DELETE', 'CODE_DELETE_SELECTION',
  'CODE_PASTE', 'CODE_CUT', 'CODE_UNDO', 'CODE_REDO', 'CODE_INDENT',
  'CODE_UNKNOWN',
]

const TERMINAL_EVENTS = [
  'TERMINAL_RUN', 'TEST_CASE_RESULT', 'TERMINAL_ERROR', 'TERMINAL_OUTPUT',
]

const CHAT_INPUT_EVENTS = [
  'CHAT_TYPE', 'CHAT_PASTE', 'CHAT_DELETE', 'CHAT_QUERY',
]

const CHAT_RESPONSE_EVENTS = [
  'CHAT_RESPONSE',
]

function getEventCategory(event) {
  if (CODE_EVENTS.includes(event.type)) return 'code'
  if (TERMINAL_EVENTS.includes(event.type)) return 'terminal'
  if (CHAT_INPUT_EVENTS.includes(event.type)) return 'chatInput'
  if (CHAT_RESPONSE_EVENTS.includes(event.type)) return 'chatResponse'
  return null
}

/**
 * Auto-segment events into labelable behavioral chunks.
 *
 * Steps:
 * 1. buildMajorSegments        — group by event category, split code on 6s gaps
 * 2. fillGapsWithThinking       — gaps ≥3s → Thinking; smaller → extend adjacent
 * 3. mergeShortTestingSegments  — testing <1.5s → absorb into neighbor
 * 4. absorbPreQueryPauses       — short thinking before chat → metadata
 * 5. applyErrorState            — implementing + ε → debugging
 * 6. applyThinkingSubtypes      — task / llm / error / code
 * 7. postProcessSegments        — fix nulls + merge consecutive same-behavior
 */
export function autoSegmentEvents(events, startTime, duration) {
  if (!events || events.length === 0) return []

  const normalized = events.map(e => ({
    ...e,
    time: e.timestamp - startTime,
  }))

  const majorSegments  = buildMajorSegments(normalized, duration)
  const allSegments    = fillGapsWithThinking(majorSegments, duration)
  const mergedTesting  = mergeShortTestingSegments(allSegments)
  const mergedSegments = absorbPreQueryPauses(mergedTesting)
  const withErrorState = applyErrorState(mergedSegments, normalized)
  const withSubtypes   = applyThinkingSubtypes(withErrorState, normalized)
  return postProcessSegments(withSubtypes, normalized)
}

// ── Step 6: Classify Thinking subtypes ───────────────────────────────
/**
 * Labels each Thinking segment with one of four subtypes based on
 * surrounding session context:
 *
 * thinking-task  — no code/run/chat activity has occurred yet
 * thinking-llm   — the segment immediately before was seekingHelp
 * thinking-error — the most recent terminal run produced an unresolved error
 * thinking-code  — residual: student reviewing own code
 */
function applyThinkingSubtypes(segments, events) {
  let hasAnyActivity = false

  let lastFailedRunTime = null
  let lastPassedRunTime = null

  const failedRunTimes = new Set()
  const passedRunTimes = new Set()

  for (const e of events) {
    if (e.type === 'TERMINAL_ERROR') {
      failedRunTimes.add(e.time)
    }
    if (e.type === 'TEST_CASE_RESULT') {
      const { passed_count, total_tests } = e.payload || {}
      if (total_tests > 0 && passed_count < total_tests)  failedRunTimes.add(e.time)
      if (total_tests > 0 && passed_count === total_tests) passedRunTimes.add(e.time)
    }
  }

  return segments.map((segment, index) => {
    if (segment.suggestedBehavior?.id !== 'thinking') {
      const bid = segment.suggestedBehavior?.id
      if (bid === 'implementing' || bid === 'debugging' || bid === 'seekingHelp') {
        hasAnyActivity = true
      }
      return segment
    }

    // Rule 1: thinking-task
    if (!hasAnyActivity) {
      hasAnyActivity = true
      return { ...segment, suggestedThinkingSubcategory: 'thinking-task' }
    }

    // Rule 2: thinking-llm
    const prevSegment = index > 0 ? segments[index - 1] : null
    if (prevSegment?.suggestedBehavior?.id === 'seekingHelp') {
      return { ...segment, suggestedThinkingSubcategory: 'thinking-llm' }
    }

    // Rule 3: thinking-error
    const recentFailedRun = [...failedRunTimes]
      .filter(t => t < segment.startTime)
      .sort((a, b) => b - a)[0]

    const recentPassedRun = [...passedRunTimes]
      .filter(t => t < segment.startTime)
      .sort((a, b) => b - a)[0]

    if (recentFailedRun !== undefined) {
      const errorIsUnresolved = recentPassedRun === undefined || recentFailedRun > recentPassedRun
      if (errorIsUnresolved) {
        return { ...segment, suggestedThinkingSubcategory: 'thinking-error' }
      }
    }

    // Rule 4: thinking-code (residual)
    return { ...segment, suggestedThinkingSubcategory: 'thinking-code' }
  })
}

// ── Step 1: Build major segments ─────────────────────────────────────
function buildMajorSegments(events, duration) {
  const INACTIVITY_SPLIT_MS = 6000
  const segments = []

  let currentSegment = null
  let currentCategory = null
  let lastEventTimeInCategory = null

  for (const event of events) {
    const category = getEventCategory(event)
    if (!category) continue

    if (category === 'chatResponse' && currentCategory && currentCategory !== 'chatInput') {
      continue
    }

    if (category === currentCategory && category === 'code' && lastEventTimeInCategory !== null) {
      const gap = event.time - lastEventTimeInCategory
      if (gap >= INACTIVITY_SPLIT_MS) {
        currentSegment.endTime = lastEventTimeInCategory
        segments.push(currentSegment)
        currentSegment = {
          id: `segment-${segments.length}-${event.time}`,
          startTime: event.time,
          endTime: null,
          behavior: null,
          suggestedBehavior: getBehaviorForCategory(category),
          suggestedThinkingSubcategory: null,
          confidence: null,
          preQueryPause: null,
        }
      }
    }

    if (category !== currentCategory) {
      if (currentSegment) {
        if (currentCategory === 'chatInput' && category === 'chatResponse') {
          currentSegment.endTime = event.time
        } else {
          currentSegment.endTime = lastEventTimeInCategory
        }
        segments.push(currentSegment)
      }

      currentCategory = category
      currentSegment = {
        id: `segment-${segments.length}-${event.time}`,
        startTime: event.time,
        endTime: null,
        behavior: null,
        suggestedBehavior: getBehaviorForCategory(category),
        suggestedThinkingSubcategory: category === 'chatResponse' ? 'thinking-llm' : null,
        confidence: null,
        preQueryPause: null,
      }
    }

    lastEventTimeInCategory = event.time
  }

  if (currentSegment) {
    currentSegment.endTime = lastEventTimeInCategory
    segments.push(currentSegment)
  }

  return segments
}

// ── Step 2: Fill gaps with Thinking ──────────────────────────────────
function fillGapsWithThinking(majorSegments, duration) {
  const MIN_THINKING_MS = 3000

  if (majorSegments.length === 0) {
    return [{
      id: `thinking-0-0`,
      startTime: 0,
      endTime: duration,
      behavior: null,
      suggestedBehavior: getBehavior('thinking'),
      suggestedThinkingSubcategory: null,
      confidence: null,
      preQueryPause: null,
    }]
  }

  const allSegments = []
  let segmentIndex = 0

  const firstMajor = majorSegments[0]
  if (firstMajor.startTime >= MIN_THINKING_MS) {
    allSegments.push({
      id: `thinking-start-0`,
      startTime: 0,
      endTime: firstMajor.startTime,
      behavior: null,
      suggestedBehavior: getBehavior('thinking'),
      suggestedThinkingSubcategory: null,
      confidence: null,
      preQueryPause: null,
    })
  } else if (firstMajor.startTime > 0) {
    firstMajor.startTime = 0
  }

  for (let i = 0; i < majorSegments.length; i++) {
    const current = majorSegments[i]
    allSegments.push(current)

    if (i < majorSegments.length - 1) {
      const next = majorSegments[i + 1]
      const gapDuration = next.startTime - current.endTime

      if (gapDuration >= MIN_THINKING_MS) {
        if (current.startTime === current.endTime) {
          current.endTime = next.startTime
        } else {
          allSegments.push({
            id: `thinking-gap-${segmentIndex++}`,
            startTime: current.endTime,
            endTime: next.startTime,
            behavior: null,
            suggestedBehavior: getBehavior('thinking'),
            suggestedThinkingSubcategory: null,
            confidence: null,
            preQueryPause: null,
          })
        }
      } else if (gapDuration > 0) {
        current.endTime = next.startTime
      }
    }
  }

  const lastMajor = allSegments[allSegments.length - 1]
  const endGap = duration - lastMajor.endTime

  if (endGap >= MIN_THINKING_MS) {
    if (lastMajor.startTime === lastMajor.endTime) {
      lastMajor.endTime = duration
    } else {
      allSegments.push({
        id: `thinking-end-${duration}`,
        startTime: lastMajor.endTime,
        endTime: duration,
        behavior: null,
        suggestedBehavior: getBehavior('thinking'),
        suggestedThinkingSubcategory: null,
        confidence: null,
        preQueryPause: null,
      })
    }
  } else if (endGap > 0) {
    lastMajor.endTime = duration
  }

  return allSegments
}

// ── Step 3: Merge short Testing segments ─────────────────────────────
function mergeShortTestingSegments(segments) {
  const MAX_MICRO_TESTING_MS = 1500

  if (segments.length < 2) return segments

  const result = []

  for (let i = 0; i < segments.length; i++) {
    const current = segments[i]
    const next = i < segments.length - 1 ? segments[i + 1] : null

    const isTesting = current.suggestedBehavior?.id === 'testing' || !current.suggestedBehavior
    const duration = current.endTime - current.startTime
    const isMicro = duration < MAX_MICRO_TESTING_MS

    if (isTesting && isMicro && next) {
      next.startTime = current.startTime
    } else if (isTesting && isMicro && !next && result.length > 0) {
      result[result.length - 1].endTime = current.endTime
    } else {
      result.push(current)
    }
  }

  return result
}

// ── Step 4: Absorb pre-query pauses ──────────────────────────────────
function absorbPreQueryPauses(segments) {
  const MAX_PRE_QUERY_PAUSE_MS = 5000

  if (segments.length < 3) return segments

  const result = []

  for (let i = 0; i < segments.length; i++) {
    const prev = result.length > 0 ? result[result.length - 1] : null
    const current = segments[i]
    const next = i < segments.length - 1 ? segments[i + 1] : null

    const isThinking = current.suggestedBehavior?.id === 'thinking'
    const pauseDuration = current.endTime - current.startTime
    const isShort = pauseDuration <= MAX_PRE_QUERY_PAUSE_MS
    const prevIsCodeOrDebug = prev && (
      prev.suggestedBehavior?.id === 'implementing' ||
      prev.suggestedBehavior?.id === 'debugging'
    )
    const nextIsChatInput = next && next.suggestedBehavior?.id === 'seekingHelp'

    if (isThinking && isShort && prevIsCodeOrDebug && nextIsChatInput) {
      next.preQueryPause = pauseDuration
      prev.endTime = current.endTime
    } else {
      result.push(current)
    }
  }

  return result
}

// ── Step 5: Apply error state ─────────────────────────────────────────
function applyErrorState(segments, events) {
  let hasUnresolvedError = false

  return segments.map(segment => {
    const segmentEvents = events.filter(e =>
      e.time >= segment.startTime && e.time < segment.endTime
    )

    const hasError = segmentEvents.some(e => e.type === 'TERMINAL_ERROR')
    const hasFailedTest = segmentEvents.some(e => {
      if (e.type !== 'TEST_CASE_RESULT') return false
      const { passed_count, total_tests } = e.payload || {}
      return total_tests > 0 && passed_count < total_tests
    })
    const hasPassedAllTests = segmentEvents.some(e => {
      if (e.type !== 'TEST_CASE_RESULT') return false
      const { passed_count, total_tests } = e.payload || {}
      return passed_count === total_tests && total_tests > 0
    })

    if (hasError || hasFailedTest) hasUnresolvedError = true
    if (hasPassedAllTests) hasUnresolvedError = false

    if (segment.suggestedBehavior?.id === 'implementing' && hasUnresolvedError) {
      return { ...segment, suggestedBehavior: getBehavior('debugging') }
    }

    return segment
  })
}

// ── Step 7: Post-process segments ─────────────────────────────────────
/**
 * 1. Fix null-behavior segments:
 *    - has terminal events → testing
 *    - otherwise → thinking
 * 2. Merge consecutive same-behavior segments into one.
 * 3. Re-index all segment IDs.
 */
function postProcessSegments(segments, events) {
  const thinkingBehavior = getBehavior('thinking')
  const testingBehavior  = getBehavior('testing')

  // Step 1: fix null-behavior segments
  let working = segments.map(seg => {
    const behaviorId = seg.suggestedBehavior?.id || seg.behavior?.id || null
    if (behaviorId === null) {
      const hasTerminal = events.some(e =>
        e.time >= seg.startTime && e.time < seg.endTime &&
        TERMINAL_EVENTS.includes(e.type)
      )
      return {
        ...seg,
        suggestedBehavior: hasTerminal
          ? (testingBehavior || thinkingBehavior)
          : thinkingBehavior,
        isSystemEvent: true,
      }
    }
    return seg
  })

  // Step 2: merge consecutive same-behavior
  if (working.length < 2) return working

  const merged = [{ ...working[0] }]
  for (let i = 1; i < working.length; i++) {
    const prev = merged[merged.length - 1]
    const curr = working[i]
    const prevId = prev.suggestedBehavior?.id || prev.behavior?.id
    const currId = curr.suggestedBehavior?.id || curr.behavior?.id

    if (prevId === currId) {
      prev.endTime = curr.endTime
      if (curr.preQueryPause) prev.preQueryPause = curr.preQueryPause
    } else {
      merged.push({ ...curr })
    }
  }

  // Step 3: re-index IDs
  return merged.map((seg, i) => ({
    ...seg,
    id: `segment-final-${i}-${seg.startTime}`,
  }))
}

// ── Helper ────────────────────────────────────────────────────────────
function getBehaviorForCategory(category) {
  switch (category) {
    case 'code':         return getBehavior('implementing')
    case 'terminal':     return getBehavior('testing')
    case 'chatInput':    return getBehavior('seekingHelp')
    case 'chatResponse': return getBehavior('thinking')
    default:             return getBehavior('unknown')
  }
}